import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Loader2, Trash2, Mail, Phone, Package, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Nouvelle", "En cours", "Devisée", "Convertie", "Perdue"];

export default function QuotesPage() {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/quotes");
            setQuotes(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const changeStatus = async (q, status) => {
        try {
            await api.patch(`/quotes/${q.id}`, null, { params: { status } });
            toast.success("Statut mis à jour");
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const remove = async (q) => {
        if (!confirm(`Supprimer la demande de ${q.company_name} ?`)) return;
        await api.delete(`/quotes/${q.id}`);
        await load();
    };

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-6xl mx-auto">
            <header>
                <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">Prospects</div>
                <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Demandes de devis</h1>
                <p className="text-sm text-slate-400 mt-1">Demandes reçues depuis le formulaire public.</p>
            </header>

            <div className="mt-8 space-y-4">
                {loading && <div className="text-slate-400"><Loader2 className="animate-spin inline mr-2" />Chargement…</div>}
                {!loading && quotes.map((q) => (
                    <article key={q.id} className="rounded-2xl border border-slate-800 bg-[#0f172a] p-5 lg:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <div className="font-display text-lg text-white font-semibold">{q.company_name}</div>
                                <div className="text-sm text-slate-300 mt-0.5">{q.contact_name}</div>
                                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
                                    <span className="flex items-center gap-1"><Mail size={12} /> {q.email}</span>
                                    {q.phone && <span className="flex items-center gap-1"><Phone size={12} /> {q.phone}</span>}
                                    <span className="font-mono text-[11px] text-slate-500">{new Date(q.created_at).toLocaleString("fr-FR")}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select
                                    value={q.status || "Nouvelle"}
                                    onChange={(e) => changeStatus(q, e.target.value)}
                                    className="py-1.5 px-3 rounded-lg bg-slate-900 border border-slate-700 text-xs text-white"
                                >
                                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button onClick={() => remove(q)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                            <div className="flex gap-2 items-start"><Package size={14} className="text-orange-400 mt-0.5" /> <div><div className="text-xs text-slate-500">Marchandise</div><div className="text-slate-200">{q.goods_type}{q.weight_kg ? ` · ${q.weight_kg} kg` : ""}</div></div></div>
                            <div className="flex gap-2 items-start"><MapPin size={14} className="text-orange-400 mt-0.5" /> <div><div className="text-xs text-slate-500">Trajet</div><div className="text-slate-200">{q.pickup_city || q.delivery_city ? `${q.pickup_city || "?"} → ${q.delivery_city || "?"}` : "—"}</div></div></div>
                            <div className="flex gap-2 items-start"><Calendar size={14} className="text-orange-400 mt-0.5" /> <div><div className="text-xs text-slate-500">Date souhaitée</div><div className="text-slate-200 font-mono">{q.desired_date || "—"}</div></div></div>
                        </div>
                        {q.message && <p className="mt-4 text-sm text-slate-300 bg-slate-900/60 rounded-lg p-3 border border-slate-800">{q.message}</p>}
                    </article>
                ))}
                {!loading && quotes.length === 0 && (
                    <div className="text-center py-14 text-slate-500 border border-dashed border-slate-700 rounded-2xl">
                        Aucune demande de devis pour le moment.
                    </div>
                )}
            </div>
        </div>
    );
}
